import queryKeys from 'constants/queryKeys';
import { useMutation, useQuery } from 'react-query';
import { useAPI } from 'services/axios';

const getDashboardData = ({ userid }: { userid: string }) => {
  return useAPI({
    url: `/dashboard?user_id=${userid}`,
    method: 'GET',
  });
};

export const useGetDashboardData = ({ userid }: { userid: string }) => {
  return useQuery([queryKeys.SCRIBE_DASHBOARD, userid], () =>
    getDashboardData({ userid })
  );
};

const getTextMacroData = () => {
  return useAPI({
    url: `/textmacros`,
    method: 'GET',
  });
};

export const useTextMacroData = () => {
  return useQuery([queryKeys.SCRIBE_DASHBOARD], () => getTextMacroData());
};

interface createTextMacroProps {
  patient_id: number;
  trigger: string;
  text: string;
}

const createTextMacro = ({
  patient_id,
  trigger,
  text,
}: createTextMacroProps) => {
  return useAPI({
    url: `/textmacros`,
    method: 'POST',
    data: {
      patient_id: +patient_id,
      trigger,
      text,
    },
  });
};

export const useCreateTextMacro = () => {
  return useMutation(createTextMacro);
};

interface updateTextMacroProps {
  patient_id: number;
  trigger: string;
  text: string;
  id: number;
}

const updateTextMacro = ({
  patient_id,
  trigger,
  text,
  id,
}: updateTextMacroProps) => {
  return useAPI({
    url: `/textmacros/${id}`,
    method: 'PUT',
    data: {
      patient_id: +patient_id,
      trigger,
      text,
    },
  });
};

export const useUpdateTextMacro = () => {
  return useMutation(updateTextMacro);
};

interface deleteTextMacroProps {
  id: number;
}

const deleteTextMacro = ({ id }: deleteTextMacroProps) => {
  return useAPI({
    url: `/textmacros/${id}`,
    method: 'DELETE',
  });
};

export const useDeleteTextMacro = () => {
  return useMutation(deleteTextMacro);
};

type MedicationProps = {
  id: number;
  patient_id: number;
  dosage: string;
  frequency: string;
  notes?: string; // Optional field
  status: 'Active' | 'Not Active'; // Enum type
  admin_plan: string;
  brand_name: string;
  generic_name: string;
};

const editMedication = ({
  dosage,
  frequency,
  notes,
  status,
  admin_plan,
  brand_name,
  generic_name,
  patient_id,
  id,
}: MedicationProps) => {
  return useAPI({
    url: `/medications/${id}`,
    method: 'PUT',
    data: {
      dosage,
      patient_id: +patient_id,
      frequency,
      notes,
      status,
      admin_plan,
      brand_name,
      generic_name,
    },
  });
};

export const useEditMedication = () => {
  return useMutation(editMedication);
};
type DiagnosisProps = {
  patient_id: number;
  diagnosis: 'string';
  dx_long: 'string';
  icd: 'string';
  status: 'Active' | 'Not Active';
  treatment: 'string';
  summary: 'string';
  userid: 'string';
  created_by: 'string';
  id: number;
};

const editDiagnosis = ({
  diagnosis,
  dx_long,
  icd,
  status,
  treatment,
  summary,
  userid,
  patient_id,
  id,
}: DiagnosisProps) => {
  return useAPI({
    url: `/diagnoses/${id}`,
    method: 'PUT',
    data: {
      diagnosis,
      dx_long,
      icd,
      status,
      treatment,
      summary,
      userid,
      patient_id: +patient_id,
    },
  });
};

export const useEditDiagnosis = () => {
  return useMutation(editDiagnosis);
};

const deleteDiagnosis = ({ id }: { id: string }) => {
  return useAPI({
    url: `/diagnoses/${id}`,
    method: 'DELETE',
  });
};

export const useDeleteDiagnosis = () => {
  return useMutation(deleteDiagnosis);
};

const deleteMedication = ({ id }: { id: string }) => {
  return useAPI({
    url: `/medications/${id}`,
    method: 'DELETE',
  });
};

export const useDeleteMedication = () => {
  return useMutation(deleteMedication);
};

const getDiagnosisDataByPatient = ({ patient_id }: { patient_id: number }) => {
  return useAPI({
    url: `/diagnoses/by-patient?patientid=${patient_id}`,
    method: 'GET',
  });
};

export const useGetDiagnosisDataByPatient = ({
  patient_id,
}: {
  patient_id: number;
}) => {
  return useQuery([queryKeys.SCRIBE_DIAGNOSIS_DATA, patient_id], () =>
    getDiagnosisDataByPatient({ patient_id })
  );
};
